import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";

import JobCategoryAddEditModal from "pages/AllCommonModal/JobCategoryAddEditModal/job-category-add-edit-modal";
import { submitFilterData } from "model/CategoryModel";
import Spinners from "components/Common/Spinner";
import { useJobCategoryContext } from "libs/context/JobCategoryContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { pascalToCamel } from "helpers/GeneralHelper";
import { submitLegendsFilterData } from "model/AuditingParameterModel";
import LegendsAddEditModal from "pages/AllCommonModal/LegendsAddEditModal/legends-add-edit-modal";
import CommonTable from "components/Common/TableContainer";
import { useAuditingParameterContext } from "libs/context/AuditingParameterContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import Loaders from "components/Common/Loaders";

const Legends = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchLegendsContextData, legendsParameterContextData, jobCategoryVersion } = useAuditingParameterContext() || {};
    const [legendsAddEditModalOpen, setLegendsAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [searchQuery, setSearchQuery] = useState("");
    const [legendsData, setLegendsData] = useState("");
    const [loading, setLoading] = useState(false);
    const [itemId, setItemId] = useState(null); // ✅ important
    const initialState = {
        title: "",
        status: true,
    };

    const [formData, setFormData] = useState(initialState);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [versionFromList, setVersionFromList] = useState();


    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return legendsData;

        return legendsData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [legendsData, searchQuery]);

    const legendsActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.LegendsAddAccess),
            canEdit: Boolean(permissionData?.LegendsUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyLegendsRowAction = useMemo(
        () => legendsActionPermissions.canEdit,
        [legendsActionPermissions]
    );

    // ✅ FIXED: set itemId FIRST, then open modal
    const handleEdit = (row) => {
        setItemId(row?.id);
        setLegendsAddEditModalOpen(true);
    };

    useEffect(() => {
        fetchLegendsContextData()
        getLegendsList();
    }, []);


    useEffect(() => {
        const valueReModified = legendsParameterContextData?.table?.columns?.map(col => ({
            Header: col?.title,
            accessor: col?.field,
        }));
        setTableMetadata(valueReModified)
    }, [legendsParameterContextData]);

    const getLegendsList = async () => {
        setLoading(true);
        let response = await submitLegendsFilterData({});
        let legendData = response?.data;
        const valueReModified = legendData?.map(item => ({
            ...item,
            ID: item?.Id,
            status: item?.status ? "active" : "deactive",
        }));
        if (response) {
            setLegendsData(valueReModified);
            setVersionFromList(response?.schemaVersion);
            setLoading(false);
        } else {
            setLegendsData([]);
            setLoading(false);
        }
    };

    const handleFilterSubmit = async (data) => {
        setLoading(true);
        let response = await submitLegendsFilterData(data);
        let filterData = response?.data;

        if (response) {
            setLegendsData(filterData);
            setLoading(false);
            setCommonFilterModalOpen(false);
        } else {
            setLegendsData([]);
            setLoading(false);
            setCommonFilterModalOpen(false);
        }
    };

    const handleSearchSubmit = async (value) => {
        setLoading(true);

        const payload = {
            search: value, // adjust key if backend expects something else
        };

        let response = await submitLegendsFilterData(payload);
        let searchData = response?.data;

        if (response) {
            setLegendsData(searchData);
        } else {
            setLegendsData([]);
        }

        setLoading(false);
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Legends</h5>
                                {legendsActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setItemId(null);
                                                setLegendsAddEditModalOpen(true);
                                                setFormData(initialState);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />

                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>


                            </Row>

                            {/* <hr /> */}
                            {/* <div className="mb-4 h4 card-title">Job Category</div> */}
                            {(!loading && legendsData?.length && tableMetaData?.length) ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyLegendsRowAction}
                                    onEdit={
                                        legendsActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isEditAccess={legendsActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"Legends"}
                                />
                            ) : 
                            // <Spinners setLoading={setLoading} />
                            <Loaders />
                            }
                        </CardBody>
                    </Card>

                    {/* Add/Edit Modal */}
                    <LegendsAddEditModal
                        setLegendsAddEditModalOpen={setLegendsAddEditModalOpen}
                        legendsAddEditModalOpen={legendsAddEditModalOpen}
                        getLegendsList={getLegendsList}
                        itemId={itemId}
                        setLegendsData={setLegendsData}
                        setFormData={setFormData}
                        formData={formData}
                        totalData={legendsData}
                    />
                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Job Category"
                        handleSubmit={handleFilterSubmit}
                        filterData={legendsParameterContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

Legends.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(Legends);
